<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { staticPageHref } from '@/router/products'
import type { Locale } from '@/i18n'
import { useAuth } from '@/composables/useAuth'
import AppButton from '@/components/ui/AppButton.vue'

const { t, locale } = useI18n()
const router = useRouter()
const {
  login,
  verifyLoginCode,
  resendLoginCode,
  cancelLogin,
  isAwaitingCode,
  pendingLoginEmail,
  isAuthenticated,
} = useAuth()

/** Connexion en deux temps quand la double authentification est active côté back :
 *  identifiants, puis code reçu par email. Sans elle, l'étape `code` n'apparaît
 *  jamais — `login()` ouvre directement la session.
 *
 *  La connexion en cours peut aussi avoir été ouverte ailleurs (carte de connexion
 *  de l'accueil ou du parcours, qui redirige ici) : on reprend alors directement
 *  à la saisie du code, puisque l'écran de code ne vit qu'à cet endroit. */
type Step = 'credentials' | 'code'
const step = ref<Step>(isAwaitingCode.value ? 'code' : 'credentials')

useHead({
  title: computed(
    () => `${step.value === 'code' ? t('auth.login.code.title') : t('auth.login.title')} — MySmerra`,
  ),
})

/** Lien localisé vers le parcours « mot de passe oublié ». */
const forgotHref = computed(() => staticPageHref('forgotPassword', locale.value as Locale))
/** Lien localisé vers l'espace personnel (cible après connexion). */
const accountHref = computed(() => staticPageHref('account', locale.value as Locale))

// Déjà connecté → on file directement vers l'espace personnel.
onMounted(() => {
  if (isAuthenticated.value) router.replace(accountHref.value)
})

const email = ref(pendingLoginEmail.value ?? '')
const password = ref('')
const code = ref('')
const showPassword = ref(false)
const isLoading = ref(false)

/** Erreurs par champ (affichées sous l'input) + erreur générale (échec de connexion). */
const emailError = ref<string | null>(null)
const passwordError = ref<string | null>(null)
const codeError = ref<string | null>(null)
const generalError = ref<string | null>(null)
/** Confirmation « un nouveau code a été envoyé » (étape code). */
const codeResent = ref(false)

/** RFC 5322 « simplifiée » : suffisant pour un contrôle de saisie côté client. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const trimmedEmail = computed(() => email.value.trim())

function validateEmail(): string | null {
  if (!trimmedEmail.value) return t('auth.login.errorEmailRequired')
  if (!EMAIL_RE.test(trimmedEmail.value)) return t('auth.login.errorEmailInvalid')
  return null
}

/** Message des échecs que les trois appels de connexion partagent (compte bloqué,
 *  quota, réseau). Les causes propres à une étape sont traitées par son handler. */
type SharedFailure = 'account_blocked' | 'rate_limited' | 'network_error'
function sharedErrorMessage(result: SharedFailure): string {
  return result === 'account_blocked'
    ? t('auth.login.errorBlocked')
    : result === 'rate_limited'
      ? t('auth.login.errorRateLimited')
      : t('auth.login.errorNetwork')
}

/** Retour à l'étape identifiants après expiration de la connexion en cours. */
function backToCredentials(message: string | null = null) {
  cancelLogin()
  step.value = 'credentials'
  code.value = ''
  password.value = ''
  codeError.value = null
  codeResent.value = false
  generalError.value = message
}

/* ----- Étape 1 : identifiants ----- */
async function submitCredentials() {
  emailError.value = validateEmail()
  passwordError.value = password.value ? null : t('auth.login.errorPasswordRequired')
  generalError.value = null
  if (emailError.value || passwordError.value) return

  isLoading.value = true
  const result = await login(trimmedEmail.value, password.value)
  isLoading.value = false

  if (result === 'success') {
    router.push(accountHref.value)
    return
  }
  if (result === 'mfa_required') {
    // Le mot de passe a été accepté : il n'a plus à rester en mémoire de la vue.
    password.value = ''
    step.value = 'code'
    return
  }

  generalError.value =
    result === 'invalid_credentials' ? t('auth.login.errorInvalid') : sharedErrorMessage(result)
}

/* ----- Étape 2 : code reçu par email ----- */
async function submitCode() {
  if (!code.value.trim()) {
    codeError.value = t('auth.login.code.errorRequired')
    return
  }
  codeError.value = null
  generalError.value = null

  isLoading.value = true
  const result = await verifyLoginCode(code.value.trim())
  isLoading.value = false

  if (result === 'success') {
    router.push(accountHref.value)
    return
  }
  if (result === 'invalid_code') {
    codeError.value = t('auth.login.code.errorInvalid')
    return
  }
  if (result === 'challenge_expired') {
    backToCredentials(t('auth.login.code.errorExpired'))
    return
  }
  generalError.value = sharedErrorMessage(result)
}

async function resendCode() {
  if (isLoading.value) return
  codeError.value = null
  generalError.value = null
  codeResent.value = false

  isLoading.value = true
  const result = await resendLoginCode()
  isLoading.value = false

  if (result === 'sent') {
    code.value = ''
    codeResent.value = true
    return
  }
  if (result === 'challenge_expired') {
    backToCredentials(t('auth.login.code.errorExpired'))
    return
  }
  generalError.value = sharedErrorMessage(result)
}
</script>

<template>
  <main id="main" class="auth" aria-labelledby="auth-title">
    <div class="auth__card">
      <!-- Étape 1 : identifiants -->
      <template v-if="step === 'credentials'">
        <header class="auth__head">
          <h1 id="auth-title" class="auth__title">{{ t('auth.login.title') }}</h1>
          <p class="auth__subtitle">{{ t('auth.login.subtitle') }}</p>
        </header>

        <form class="auth__form" novalidate @submit.prevent="submitCredentials">
          <!-- Email -->
          <div class="auth__field">
            <label for="login-email">{{ t('auth.login.email') }}</label>
            <input
              id="login-email"
              v-model="email"
              type="email"
              autocomplete="email"
              inputmode="email"
              :placeholder="t('auth.login.emailPlaceholder')"
              :disabled="isLoading"
              :aria-invalid="emailError ? 'true' : undefined"
              :aria-describedby="emailError ? 'login-email-error' : undefined"
              @input="emailError = null"
            />
            <p v-if="emailError" id="login-email-error" class="auth__error" role="alert">
              {{ emailError }}
            </p>
          </div>

          <!-- Mot de passe + œil barré / révélé -->
          <div class="auth__field">
            <label for="login-password">{{ t('auth.login.password') }}</label>
            <div class="auth__input-wrap">
              <input
                id="login-password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                :disabled="isLoading"
                :aria-invalid="passwordError ? 'true' : undefined"
                :aria-describedby="passwordError ? 'login-password-error' : undefined"
                @input="passwordError = null"
              />
              <button
                type="button"
                class="auth__reveal"
                :aria-label="showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')"
                :aria-pressed="showPassword"
                @click="showPassword = !showPassword"
              >
                <!-- Œil ouvert (mot de passe visible) -->
                <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                </svg>
                <!-- Œil barré (mot de passe masqué, défaut) -->
                <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <p v-if="passwordError" id="login-password-error" class="auth__error" role="alert">
              {{ passwordError }}
            </p>
          </div>

          <a :href="forgotHref" class="auth__link">{{ t('auth.login.forgot') }}</a>

          <p v-if="generalError" class="auth__error auth__error--banner" role="alert">
            {{ generalError }}
          </p>

          <AppButton type="submit" variant="dark" size="md" block :ring="!isLoading">
            {{ isLoading ? t('auth.login.submitting') : t('auth.login.submit') }}
          </AppButton>
        </form>
      </template>

      <!-- Étape 2 : code de double authentification reçu par email -->
      <template v-else>
        <header class="auth__head">
          <h1 id="auth-title" class="auth__title">{{ t('auth.login.code.title') }}</h1>
          <p class="auth__subtitle">{{ t('auth.login.code.intro', { email: trimmedEmail }) }}</p>
        </header>

        <form class="auth__form" novalidate @submit.prevent="submitCode">
          <div class="auth__field">
            <label for="login-code">{{ t('auth.login.code.label') }}</label>
            <input
              id="login-code"
              v-model="code"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              :placeholder="t('auth.login.code.placeholder')"
              :disabled="isLoading"
              :aria-invalid="codeError ? 'true' : undefined"
              :aria-describedby="codeError ? 'login-code-error' : undefined"
              @input="codeError = null; codeResent = false"
            />
            <p v-if="codeError" id="login-code-error" class="auth__error" role="alert">
              {{ codeError }}
            </p>
            <p v-else-if="codeResent" class="auth__notice" role="status">
              {{ t('auth.login.code.resent') }}
            </p>
          </div>

          <p v-if="generalError" class="auth__error auth__error--banner" role="alert">
            {{ generalError }}
          </p>

          <AppButton type="submit" variant="dark" size="md" block :ring="!isLoading" :disabled="isLoading">
            {{ isLoading ? t('auth.login.code.submitting') : t('auth.login.code.submit') }}
          </AppButton>

          <button type="button" class="auth__link-btn" :disabled="isLoading" @click="resendCode">
            {{ t('auth.login.code.resend') }}
          </button>
          <button type="button" class="auth__link-btn" :disabled="isLoading" @click="backToCredentials()">
            {{ t('auth.login.code.back') }}
          </button>
        </form>
      </template>
    </div>
  </main>
</template>

<style scoped>
.auth {
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: clamp(2rem, 6vw, 4rem) var(--layout-gutter);
}
.auth__card {
  width: 100%;
  max-width: 26rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  background: var(--color-white);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}
.auth__head {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  text-align: center;
}
.auth__title {
  font-size: var(--text-3xl);
  font-weight: 800;
  line-height: var(--leading-tight);
  color: var(--color-chocolate);
}
.auth__subtitle {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.auth__form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.auth__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.auth__field label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
}
.auth__field input {
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--bg-soft);
  font-size: var(--text-base);
  transition: border-color var(--dur-fast), background var(--dur-fast);
}
.auth__field input:focus {
  outline: none;
  border-color: var(--primary);
  background: var(--color-white);
}
.auth__field input[aria-invalid='true'] {
  border-color: var(--color-cherry);
}

/* Champ mot de passe : input + bouton œil superposé à droite. */
.auth__input-wrap {
  position: relative;
  display: flex;
}
.auth__input-wrap input {
  padding-right: 3rem;
}
.auth__reveal {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 3rem;
  display: grid;
  place-items: center;
  background: none;
  border: 0;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--dur-fast) var(--ease-out);
}
.auth__reveal:hover { color: var(--color-chocolate); }
.auth__reveal:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

.auth__link {
  align-self: flex-end;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  transition: color var(--dur-fast);
}
.auth__link:hover { color: var(--color-cherry); }

.auth__link-btn {
  align-self: center;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0;
  transition: color var(--dur-fast);
}
.auth__link-btn:hover { color: var(--color-cherry); }
.auth__link-btn:disabled { opacity: 0.6; cursor: default; }

.auth__error {
  font-size: var(--text-sm);
  color: var(--color-cherry);
}
.auth__error--banner {
  background: color-mix(in oklab, var(--color-cherry) 8%, white);
  padding: 0.625rem 0.75rem;
  border-radius: var(--radius-sm);
}
.auth__notice {
  font-size: var(--text-sm);
  color: var(--color-chocolate);
}
</style>
